import React from 'react';
import { Select, Input, Card } from 'components';
import { CreateListingCardProps, PropertyDetailsFields } from '../types';
import { StyledGrid } from './StyledComponents';

type FieldsMapResidentialFields = {
  sale: PropertyDetailsFields[];
  lease: PropertyDetailsFields[];
};

type FieldsMapType = {
  freehold: FieldsMapResidentialFields;
  land: FieldsMapResidentialFields;
  income: FieldsMapResidentialFields;
  condo: PropertyDetailsFields[];
  commercial: PropertyDetailsFields[];
};

const fieldsMapResidentialFields: FieldsMapResidentialFields = {
  sale: [
    'total_rooms',
    'bedrooms',
    'bathrooms',
    'rooms_plus',
    'bedrooms_plus',
    'kitchens',
    'basement',
    'heat_source',
    'heat_type',
    'ac',
    'age',
    'apx_sqft',
    'sqft',
  ],
  lease: [
    'total_rooms',
    'bedrooms',
    'bathrooms',
    'rooms_plus',
    'bedrooms_plus',
    'kitchens',
    'basement',
    'laundry_access',
    'heat_source',
    'heat_type',
    'ac',
    'age',
    'apx_sqft',
    'sqft',
  ],
};
const fieldsMap: FieldsMapType = {
  freehold: fieldsMapResidentialFields,
  land: fieldsMapResidentialFields,
  income: fieldsMapResidentialFields,
  condo: [
    'total_rooms',
    'bedrooms',
    'bathrooms',
    'rooms_plus',
    'bedrooms_plus',
    'kitchens',
    'heat_source',
    'heat_type',
    'ac',
    'age',
    'apx_sqft',
    'sqft',
  ],
  commercial: [
    'bathrooms',
    'kitchens',
    'heat_source',
    'heat_type',
    'ac',
    'age',
    'water',
    'utilities',
  ],
};

type PropertyDetailsProps = CreateListingCardProps<PropertyDetailsFields>;

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  type,
  subtype,
  total_rooms,
  bedrooms,
  bathrooms,
  rooms_plus,
  bedrooms_plus,
  kitchens,
  basement,
  laundry_access,
  heat_source,
  heat_type,
  ac,
  age,
  apx_sqft,
  sqft,
  water,
  utilities,
  setListing,
}) => {
  const getFields = () => {
    if (['freehold', 'land', 'income'].includes(type!)) {
      return fieldsMap[type as 'freehold' | 'land' | 'income'][subtype!] || [];
    }
    if (['condo', 'commercial'].includes(type!)) {
      return fieldsMap[type as 'condo' | 'commercial'];
    }
    return [];
  };

  const fields = getFields();

  return (
    <Card type="primary" title="Property Details" id="propertyDetails">
      <StyledGrid $columns={3}>
        {fields.includes('total_rooms') && (
          <Input
            label="Rooms"
            value={total_rooms}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, total_rooms: value }))
            }
            data-cy="listing-total-rooms"
          />
        )}
        {fields.includes('bedrooms') && (
          <Input
            label="Bedrooms"
            value={bedrooms}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, bedrooms: value }))
            }
            data-cy="listing-bedrooms"
          />
        )}
        {fields.includes('bathrooms') && (
          <Input
            label="Bathrooms"
            value={bathrooms}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, bathrooms: value }))
            }
            data-cy="listing-bathrooms"
          />
        )}
        {fields.includes('rooms_plus') && (
          <Input
            label="Rooms +"
            value={rooms_plus}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, rooms_plus: value }))
            }
            data-cy="listing-rooms-plus"
          />
        )}
        {fields.includes('bedrooms_plus') && (
          <Input
            label="Bedrooms +"
            value={bedrooms_plus}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, bedrooms_plus: value }))
            }
            data-cy="listing-bedrooms-plus"
          />
        )}
        {fields.includes('kitchens') && (
          <Input
            label="Kitchens"
            value={kitchens}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, kitchens: value }))
            }
            data-cy="listing-kitchens"
          />
        )}
        {fields.includes('basement') && (
          <Select
            allowClear
            label="Basement"
            value={basement}
            onChange={value =>
              setListing?.(prev => ({ ...prev, basement: value }))
            }
            options={[
              { label: 'Apartment', value: 'Apartment' },
              { label: 'Crawl Space', value: 'Crawl Space' },
              { label: 'Finished', value: 'Finished' },
              {
                label: 'Finished with Walk-Out',
                value: 'Finished with Walk-Out',
              },
              { label: 'Full', value: 'Full' },
              { label: 'Half', value: 'Half' },
              { label: 'None', value: 'None' },
              { label: 'Other', value: 'Other' },
              { label: 'Partial Basement', value: 'Partial Basement' },
              { label: 'Partially Finished', value: 'Partially Finished' },
              { label: 'Separate Entrance', value: 'Separate Entrance' },
              { label: 'Unfinished', value: 'Unfinished' },
              { label: 'Walk-Out', value: 'Walk-Out' },
              { label: 'Walk-Up', value: 'Walk-Up' },
            ]}
            data-cy="listing-basement"
          />
        )}
        {fields.includes('laundry_access') && (
          <Select
            allowClear
            label="Laundry Access"
            value={laundry_access}
            onChange={value =>
              setListing?.(prev => ({ ...prev, laundry_access: value }))
            }
            options={[
              { label: 'Coin', value: 'Coin' },
              { label: 'Ensuite', value: 'Ensuite' },
              { label: 'None', value: 'None' },
              { label: 'Shared', value: 'Shared' },
              { label: 'On site', value: 'On site' },
            ]}
          />
        )}
        {fields.includes('heat_source') && (
          <Select
            allowClear
            label="Heat Source"
            value={heat_source}
            onChange={value =>
              setListing?.(prev => ({ ...prev, heat_source: value }))
            }
            options={[
              { label: 'Electric', value: 'Electric' },
              { label: 'Gas', value: 'Gas' },
              { label: 'Grnd Srce', value: 'Grnd Srce' },
              { label: 'Oil', value: 'Oil' },
              { label: 'Other', value: 'Other' },
              { label: 'Propane', value: 'Propane' },
              { label: 'Solar', value: 'Solar' },
              { label: 'Wood', value: 'Wood' },
            ]}
            data-cy="listing-heat-source"
          />
        )}
        {fields.includes('heat_type') && (
          <Select
            allowClear
            label="Heat Type"
            value={heat_type}
            onChange={value =>
              setListing?.(prev => ({ ...prev, heat_type: value }))
            }
            options={
              type === 'commercial'
                ? [
                    {
                      label: 'Gas Forced Air Open',
                      value: 'Gas Forced Air Open',
                    },
                    {
                      label: 'Gas Forced Air Closd',
                      value: 'Gas Forced Air Closd',
                    },
                    { label: 'Gas Hot Water', value: 'Gas Hot Water' },
                    { label: 'Elec Forced Air', value: 'Elec Forced Air' },
                    { label: 'Elec Hot Water', value: 'Elec Hot Water' },
                    { label: 'Oil Forced Air', value: 'Oil Forced Air' },
                    { label: 'Oil Steam', value: 'Oil Steam' },
                    { label: 'Oil Hot Water', value: 'Oil Hot Water' },
                    { label: 'Steam Radiators', value: 'Steam Radiators' },
                    { label: 'Water Radiators', value: 'Water Radiators' },
                    { label: 'Baseboard', value: 'Baseboard' },
                    { label: 'Propane Gas', value: 'Propane Gas' },
                    { label: 'Radiant', value: 'Radiant' },
                    { label: 'Solar', value: 'Solar' },
                    { label: 'None', value: 'None' },
                    { label: 'Woodburning', value: 'Woodburning' },
                    { label: 'Other', value: 'Other' },
                  ]
                : [
                    { label: 'Baseboard', value: 'Baseboard' },
                    { label: 'Forced Air', value: 'Forced Air' },
                    { label: 'Heat Pump', value: 'Heat Pump' },
                    { label: 'Other', value: 'Other' },
                    { label: 'Radiant', value: 'Radiant' },
                    { label: 'Water', value: 'Water' },
                  ]
            }
            data-cy={`listing-${
              type === 'commercial' ? 'commercial-' : ''
            }heat-type`}
          />
        )}
        {fields.includes('ac') && (
          <Select
            allowClear
            label="A/C"
            value={ac}
            onChange={value => setListing?.(prev => ({ ...prev, ac: value }))}
            options={[
              { label: 'Central Air', value: 'Central Air' },
              { label: 'None', value: 'None' },
              { label: 'Other', value: 'Other' },
              { label: 'Wall Unit', value: 'Wall Unit' },
              { label: 'Window Unit', value: 'Window Unit' },
            ]}
            data-cy="listing-ac"
          />
        )}
        {fields.includes('apx_sqft') && (
          <Select
            allowClear
            label="Apx Sqft"
            value={apx_sqft}
            onChange={value =>
              setListing?.(prev => ({ ...prev, apx_sqft: value }))
            }
            options={[
              { label: '< 700', value: '< 700' },
              { label: '700-1100', value: '700-1100' },
              { label: '1100-1500', value: '1100-1500' },
              { label: '1500-2000', value: '1500-2000' },
              { label: '2000-2500', value: '2000-2500' },
              { label: '2500-3000', value: '2500-3000' },
              { label: '3000-3500', value: '3000-3500' },
              { label: '3500-5000', value: '3500-5000' },
              { label: '5000+', value: '5000+' },
            ]}
            data-cy="listing-apx-sqft"
          />
        )}
        {fields.includes('sqft') && (
          <Input
            label="Sqft"
            value={sqft}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, sqft: value }))
            }
            data-cy="listing-sqft"
          />
        )}
        {fields.includes('water') && (
          <Select
            allowClear
            label="Water"
            value={water}
            onChange={value =>
              setListing?.(prev => ({ ...prev, water: value }))
            }
            options={[
              { label: 'Municipal', value: 'Municipal' },
              { label: 'Well', value: 'Well' },
              { label: 'None', value: 'None' },
              { label: 'Other', value: 'Other' },
              { label: 'Both', value: 'Both' },
            ]}
            data-cy="listing-commercial-water"
          />
        )}
        {fields.includes('utilities') && (
          <Input
            label="Utilities"
            value={utilities}
            onChange={({ target: { value } }) =>
              setListing?.(prev => ({ ...prev, utilities: value }))
            }
            data-cy="listing-commercial-utilities"
          />
        )}
        {fields.includes('age') && (
          <Select
            allowClear
            label="Apx Age"
            value={age}
            onChange={value => setListing?.(prev => ({ ...prev, age: value }))}
            options={[
              { label: 'New', value: 'New' },
              { label: '0-5', value: '0-5' },
              { label: '6-10', value: '6-10' },
              { label: '11-15', value: '11-15' },
              { label: '16-30', value: '16-30' },
              { label: '31-50', value: '31-50' },
              { label: '51-99', value: '51-99' },
              { label: '100+', value: '100+' },
            ]}
            data-cy="listing-age"
          />
        )}
      </StyledGrid>
    </Card>
  );
};

export default React.memo(PropertyDetails);
